import { uuid4 } from '@sentry/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { utilsApi } from '../../api/utils';

const useCancelableRequest = ({ endpoint, params, shouldSkip = false }) => {
  const dispatch = useDispatch();
  const [requestIdList, setRequestIdList] = useState([]);
  const requestIdListRef = useRef(requestIdList);
  requestIdListRef.current = requestIdList;
  const prevShouldSkipRef = useRef(shouldSkip);
  const prevParamsRef = useRef(params);
  const latestRequestIdRef = useRef(null);
  const [cancelJob] = utilsApi.useJobCancellationByIdMutation();

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  const jobCancellation = useCallback(
    (ids) => {
      if (ids.length > 0) {
        cancelJob(ids.map((ele) => ({ request_id: ele })));
      }
      setRequestIdList((prev) => prev.filter((id) => !ids.includes(id)));
    },
    [cancelJob]
  );

  const call = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);

    jobCancellation(requestIdList);

    const newId = uuid4();
    setRequestIdList([newId]);

    latestRequestIdRef.current = newId;

    const result = await dispatch(
      endpoint.initiate({
        ...params,
        requestId: newId,
      })
    );

    setRequestIdList((prev) => prev.filter((id) => id !== newId));
    if (latestRequestIdRef.current === newId) {
      if ('data' in result) {
        setData(result.data);
      } else {
        setError(result.error);
      }
      setIsLoading(false);
    }

    return result;
  }, [dispatch, endpoint, jobCancellation, params, requestIdList]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const handleUnload = () => {
      jobCancellation(requestIdList);
    };
    const handlePopState = () => {
      jobCancellation(requestIdList);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [jobCancellation, requestIdList]);

  useEffect(
    () => () => {
      if (requestIdListRef.current.length > 0) {
        jobCancellation(requestIdListRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (
      !shouldSkip &&
      (prevShouldSkipRef.current !== shouldSkip ||
        JSON.stringify(prevParamsRef.current) !== JSON.stringify(params))
    ) {
      prevShouldSkipRef.current = shouldSkip;
      prevParamsRef.current = params;
      call();
    }
  }, [params, shouldSkip, call]);

  return {
    data,
    isLoading,
    error,
    requestIdList,
    jobCancellation,
    refetch: call,
  };
};

export default useCancelableRequest;
